import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import TrueFocus from "./TrueFocus";
import "./footer.css";
import { useLanguage } from "../../i18n/LanguageContext";

export default function Footer() {
  const { tr } = useLanguage();
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // EmailJS Configuration
  const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_3anduw9";
  const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_zpiq81y";
  const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "EeoCxVL2v5wIIhpS6";

  useEffect(() => {
    // Initialize EmailJS with public key
    if (EMAILJS_PUBLIC_KEY) {
      emailjs.init(EMAILJS_PUBLIC_KEY);
    }
  }, []);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes("@")) {
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const templateParams = {
        from_email: email,
        to_email: "metagent.studio@gmail.com",
        message: "Newsletter subscription request",
        subject: "Newsletter Subscription",
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );

      setSubmitStatus("success");
      setEmail("");
    } catch (error) {
      console.error("Error sending email:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="footer">
      <div className="footer-container">
        {/* Top Section */}
        <div className="footer-top">
          {/* Newsletter Section */}
          <div className="footer-newsletter">
            <h2 className="newsletter-heading">
              {tr("Never miss what's next", "Jangan lewatkan info terbaru")}
            </h2>
            <form className="newsletter-form" onSubmit={handleSubmit}>
              <div className="newsletter-input-group">
                {/* <label htmlFor="footer-email" className="newsletter-label">
                  Your email
                </label> */}
                <input
                  type="email"
                  id="footer-email"
                  className="newsletter-input"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder={tr("Your email", "Email kamu")}
                  required
                />
                <button
                  type="submit"
                  className="newsletter-submit"
                  disabled={isSubmitting}
                >
                  {tr("SUBMIT", "KIRIM")}
                  <span className="submit-arrow">→</span>
                </button>
              </div>
              <p className="newsletter-description">
                {tr(
                  "By submitting your email, you'll be the first to know about upcoming updates for Metagent. You can unsubscribe at any time.",
                  "Dengan mengirim email, kamu akan jadi yang pertama tahu update terbaru Metagent. Kamu bisa berhenti berlangganan kapan saja."
                )}
              </p>
              {submitStatus === "success" && (
                <p className="newsletter-message success">
                  {tr("Thank you for subscribing!", "Terima kasih sudah berlangganan!")}
                </p>
              )}
              {submitStatus === "error" && (
                <p className="newsletter-message error">
                  {tr("Please enter a valid email address.", "Masukkan alamat email yang valid.")}
                </p>
              )}
            </form>
          </div>

          {/* Navigation Links */}
          <div className="footer-nav">
            <div className="nav-column">
              <h3 className="nav-heading">{tr("SOCIAL", "SOSIAL")}</h3>
              <ul className="nav-links">
                <li>
                  <a href="https://www.tiktok.com/@metagent.studio" target="_blank" rel="noopener noreferrer">
                    Tiktok
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/metagent.studio/" target="_blank" rel="noopener noreferrer">
                    Instagram
                  </a>
                </li>
              </ul>
            </div>

            <div className="nav-column">
              <h3 className="nav-heading">{tr("PAGES", "HALAMAN")}</h3>
              <ul className="nav-links">
                <li>
                  <Link to="/about">{tr("About Us", "Tentang Kami")}</Link>
                </li>
                <li>
                  <Link to="/portfolio">Portfolio</Link>
                </li>
                <li>
                  <Link to="/use-cases">{tr("Use Cases", "Studi Kasus")}</Link>
                </li>
                <li>
                  <Link to="/pricing">{tr("Pricing", "Harga")}</Link>
                </li>
                <li>
                  <Link to="/how-we-work">{tr("How We Work", "Cara Kerja")}</Link>
                </li>
                <li>
                  <Link to="/faq">FAQ</Link>
                </li>
              </ul>
            </div>

            <div className="nav-column">
              <h3 className="nav-heading">{tr("CONTACT", "KONTAK")}</h3>
              <ul className="nav-links">
                <li>
                  <a href="#contact-us">{tr("Talk to Us", "Hubungi Kami")}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="footer-bottom">
          <div className="footer-copyright">
            <p>
              {new Date().getFullYear()}© <span className="copyright-separator">|</span>{" "}
              <Link to="/permissions-terms-privacy" className="footer-legal-link">
                {tr("Permissions & Terms Privacy", "Izin & Ketentuan Privasi")}
              </Link>{" "}
              <span className="copyright-separator">|</span>{" "}
              <Link to="/policy" className="footer-legal-link">
                {tr("Policy", "Kebijakan")}
              </Link>
            </p>
          </div>
          <div className="footer-logo">
            <TrueFocus 
              sentence="METAGENT"
              separator=""
              blurAmount={5}
              borderColor="#CCFF00"
              glowColor="rgba(204, 255, 0, 0.6)"
              animationDuration={0.5}
              pauseBetweenAnimations={1}
              fontSize="3vw"
              fontWeight="700"
              color="#ffffff"
            />
          </div>
        </div>
      </div>
    </footer>
  );
}

